import React from 'react';
import { useTranslation } from 'react-i18next';
import './MethodSection.css';

const MethodSection: React.FC = () => {
  const { t } = useTranslation();

  const changes = [
    'decisions',
    'docket',
    'develops',
    'venue',
    'bothSides',
    'judgment',
    'findings',
    'consistent',
  ];

  const steps = ['materials', 'review', 'capture', 'findings'];

  return (
    <div className="method-section">
      {/* ===== What this is ===== */}
      <section className="what-this-is">
        <div className="container">
          <div className="section-header">
            <h2>{t('method.whatThisIs.title')}</h2>
            <p className="section-lead">{t('method.whatThisIs.lead')}</p>
          </div>

          <div className="triplet">
            <div className="triplet-card">
              <span className="triplet-num">01</span>
              <h4>{t('method.whatThisIs.triplet.provide.title')}</h4>
              <p>{t('method.whatThisIs.triplet.provide.text')}</p>
            </div>
            <div className="triplet-connector" aria-hidden="true" />
            <div className="triplet-card">
              <span className="triplet-num">02</span>
              <h4>{t('method.whatThisIs.triplet.runs.title')}</h4>
              <p>{t('method.whatThisIs.triplet.runs.text')}</p>
            </div>
            <div className="triplet-connector" aria-hidden="true" />
            <div className="triplet-card">
              <span className="triplet-num">03</span>
              <h4>{t('method.whatThisIs.triplet.receive.title')}</h4>
              <p>{t('method.whatThisIs.triplet.receive.text')}</p>
            </div>
          </div>

          <div className="credibility-block">
            <span className="credibility-rule" aria-hidden="true" />
            <p className="credibility-line">{t('method.whatThisIs.credibility')}</p>
            <span className="credibility-rule" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ===== Why it matters ===== */}
      <section className="why-it-matters">
        <div className="container">
          <div className="why-content">
            <h2 className="why-headline">{t('method.whyItMatters.title')}</h2>
            <p className="why-support">{t('method.whyItMatters.support')}</p>

            <figure className="why-pullquote">
              <span className="pullquote-mark" aria-hidden="true">“</span>
              <blockquote>{t('method.whyItMatters.pullquote')}</blockquote>
            </figure>

            <p className="why-close">{t('method.whyItMatters.close')}</p>
          </div>
        </div>
      </section>

      {/* ===== What changes ===== */}
      <section className="what-changes">
        <div className="container">
          <div className="section-header centered">
            <h2>{t('method.whatChanges.title')}</h2>
            <p className="section-lead">{t('method.whatChanges.lead')}</p>
          </div>

          <div className="changes-grid">
            {changes.map((key) => (
              <div key={key} className="change-card">
                <div className="card-inner">
                  <div className="card-face card-front">
                    <span className="card-mark" aria-hidden="true" />
                    <h3>{t(`method.whatChanges.items.${key}.title`)}</h3>
                    <p className="card-lead">{t(`method.whatChanges.items.${key}.lead`)}</p>
                  </div>
                  <div className="card-face card-back">
                    <span className="card-mark" aria-hidden="true" />
                    <h4 className="card-back-title">{t(`method.whatChanges.items.${key}.title`)}</h4>
                    <p className="card-body">{t(`method.whatChanges.items.${key}.body`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Method process (centerpiece) ===== */}
      <section className="method-process">
        <div className="container">
          <div className="section-header centered">
            <div className="process-eyebrow" aria-hidden="true">
              <span className="eyebrow-rule" />
              <span className="eyebrow-mark">✦</span>
              <span className="eyebrow-rule" />
            </div>
            <h2>{t('method.process.title')}</h2>
            <p className="section-lead light">{t('method.process.lead')}</p>
          </div>

          <div className="process-flow">
            <div className="process-line" aria-hidden="true" />
            {steps.map((key, idx) => (
              <div key={key} className="process-step">
                <div className="step-circle">
                  <span className="step-num">{idx + 1}</span>
                </div>
                <h4>{t(`method.process.steps.${key}.title`)}</h4>
                <p>{t(`method.process.steps.${key}.text`)}</p>
              </div>
            ))}
          </div>

          <div className="process-footer">
            <div className="footer-divider" aria-hidden="true" />
            <p>
              <strong>{t('method.process.footer.line1')}</strong>
              <br />
              {t('method.process.footer.line2')}
            </p>
          </div>
        </div>
      </section>

      {/* ===== Positioning ===== */}
      <section className="positioning">
        <div className="container">
          <div className="positioning-content">
            <div className="positioning-eyebrow" aria-hidden="true">
              <span className="eyebrow-rule" />
              <span className="eyebrow-mark">✦</span>
              <span className="eyebrow-rule" />
            </div>
            <h2>{t('method.positioning.title')}</h2>
            <div className="positioning-pillars">
              <div className="pillar">
                <span className="pillar-numeral" aria-hidden="true">I</span>
                <span className="pillar-text">{t('method.positioning.pillars.venue')}</span>
              </div>
              <div className="pillar-divider" aria-hidden="true" />
              <div className="pillar">
                <span className="pillar-numeral" aria-hidden="true">II</span>
                <span className="pillar-text">{t('method.positioning.pillars.calibration')}</span>
              </div>
              <div className="pillar-divider" aria-hidden="true" />
              <div className="pillar">
                <span className="pillar-numeral" aria-hidden="true">III</span>
                <span className="pillar-text">{t('method.positioning.pillars.repeat')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MethodSection;
